import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";

const themes = {
  dark: {
    vars: {
      "--output-bg": "#111417",
      "--output-text": "#00e676",
      "--table-border": "rgba(255,255,255,0.1)",
      "--table-header": "#00e676",
    },
  },
  github: {
    vars: {
      "--output-bg": "#ffffff",
      "--output-text": "#0969da",
      "--table-border": "rgba(0,0,0,0.1)",
      "--table-header": "#0969da",
    },
  },
  dracula: {
    vars: {
      "--output-bg": "#1e1f29",
      "--output-text": "#50fa7b",
      "--table-border": "rgba(255,255,255,0.1)",
      "--table-header": "#50fa7b",
    },
  },
  monokai: {
    vars: {
      "--output-bg": "#33352e",
      "--output-text": "#a6e22e",
      "--table-border": "rgba(255,255,255,0.1)",
      "--table-header": "#a6e22e",
    },
  },
};

export default function SqlCodeEditorOutput({
  code,
  sqlDb,
  loading = false,
  themeName: externalTheme
}) {
  const [output, setOutput] = useState([]);
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";
  const themeName = externalTheme !== undefined ? externalTheme : (isDarkMode ? "dark" : "github");

  useEffect(() => {
    const vars = themes[themeName]?.vars || (isDarkMode ? themes.dark.vars : themes.github.vars);
    Object.entries(vars).forEach(([key, val]) =>
      document.documentElement.style.setProperty(key, val)
    );
  }, [themeName, isDarkMode]);

  const { vars } = themes[themeName] || (isDarkMode ? themes.dark : themes.github);
  const outputBg = vars["--output-bg"];
  const outputText = vars["--output-text"];
  const tableBorder = vars["--table-border"];
  const tableHeader = vars["--table-header"];

  useEffect(() => {
    if (!code || !code.trim() || !sqlDb) {
      setOutput([]);
      return;
    }

    const runQuery = () => {
      try {
        const result = sqlDb.exec(code);
        setOutput(result.length ? result : [{ columns: [], values: [["✅ Query executed successfully"]] }]);
      } catch (err) {
        setOutput([{ columns: [], values: [[`❌ Error: ${err.message}`]] }]);
      }
    };

    runQuery();
  }, [code, sqlDb]);

  // Format output with line numbers
  const formatOutput = (outputTables) => {
    if (!outputTables || outputTables.length === 0) return null;
    
    return outputTables.map((table, tableIndex) => {
      if (table.columns.length === 0) {
        // Message output (no table structure)
        return (
          <Box
            key={tableIndex}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              fontFamily: "'Fira Code', monospace",
              fontSize: "0.9rem",
              lineHeight: 1.6,
              mb: 1,
              color: outputText,
            }}
          >
            {table.values[0]?.[0] || ""}
          </Box>
        );
      }

      // Table output
      return (
        <Box
          key={tableIndex}
          sx={{
            mb: 2,
            overflowX: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "'Fira Code', monospace",
              fontSize: "0.9rem",
            }}
          >
            <thead>
              <tr>
                {table.columns.map((col, colIndex) => (
                  <th
                    key={colIndex}
                    style={{
                      borderBottom: `2px solid ${tableBorder}`,
                      textAlign: "left",
                      padding: "8px 12px",
                      color: tableHeader,
                      fontWeight: 600,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.values.map((row, rIdx) => (
                <tr
                  key={rIdx}
                  style={{
                    borderBottom: `1px solid ${tableBorder}`,
                    "&:hover": {
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                    },
                  }}
                >
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      style={{
                        padding: "6px 12px",
                        color: outputText,
                      }}
                    >
                      {cell !== null && cell !== undefined ? String(cell) : "NULL"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 2,
        p: 1.5,
        background: outputBg,
        color: outputText,
        fontFamily: "'Fira Code', monospace",
        fontSize: "0.9rem",
        whiteSpace: "pre-wrap",
        overflowY: "auto",
        border: `1px solid ${
          isDarkMode ? "rgba(255,255,255,0.1)" : muiTheme.palette.divider
        }`,
        transition: "background 0.4s ease, color 0.3s ease, border-color 0.3s ease",
      }}
    >
      {loading ? (
        <Typography sx={{ color: outputText }}>⏳ Running SQL query...</Typography>
      ) : output.length > 0 ? (
        formatOutput(output)
      ) : (
        <Typography sx={{ color: muiTheme.palette.text.secondary }}>
          💡 Output will appear here after execution
        </Typography>
      )}
    </Box>
  );
}

